import classNames from "classnames";
import { camelize } from "humps";
import { CustomValuesEditField } from "PFApp/components/custom_values_edit_field";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Button } from "PFComponents/button";
import { Select } from "PFComponents/select";
import SidePanel from "PFComponents/side_panel/side_panel";
import { getProfileName } from "PFCore/helpers/profile";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useCustomValuesInvalidate } from "PFCore/hooks/queries/custom_fields/admin";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { fetchProfilesOptions } from "PFCore/services/profile";
import { Option } from "PFTypes";
import { CustomType, FeatureFlag, Profile } from "PFTypes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import css from "../organisation_data.module.scss";

type OrganisationDataEditProps = {
  profile: Profile;
  basicCustomTypes: CustomType[];
  isLoading: boolean;
  errors: string | string[];
  onProfileUpdate: (profileFieldsToUpdate: Partial<Profile>) => Promise<void>;
  onClose: () => void;
};

export const OrganisationDataEdit = ({
  profile,
  basicCustomTypes,
  isLoading,
  errors,
  onProfileUpdate,
  onClose
}: OrganisationDataEditProps) => {
  const { t } = useTranslation(["profiles", "translation"]);
  const { data: currentProfile } = useCurrentProfile();
  const { showGrowl } = useGrowlStoreActions();
  const { invalidate: invalidateCustomValues } = useCustomValuesInvalidate();
  const isReportsToEnabled = useIsFeatureEnabled(FeatureFlag.ReportsTo);

  const { supervisor } = profile;

  const [supervisorValue, setSupervisorValue] = useState(supervisor || null);
  const [profileFieldsToUpdate, setProfileFieldsToUpdate] = useState({});

  const renderFooter = () => (
    <div className={css.editFooter}>
      <Button
        aria-label={t("profiles:show.parts.closeEditOrganisationData")}
        kind="secondary"
        text={t("translation:cancel")}
        onClick={onClose}
      />
      <Button
        kind="primary"
        aria-label={t("profiles:show.parts.saveEditOrganisationData")}
        text={t("translation:save")}
        onClick={() =>
          onProfileUpdate(profileFieldsToUpdate).then(() => {
            setProfileFieldsToUpdate({});
            invalidateCustomValues();

            showGrowl({
              message: t("profiles:show.parts.organisationDataUpdated"),
              kind: "success"
            });
          })
        }
        disabled={isLoading}
      />
    </div>
  );

  return (
    <SidePanel
      show
      fullHeight
      isOnTop
      size="medium"
      title={t("profiles:show.parts.editOrganisationData")}
      onClose={onClose}
      footerRenderer={renderFooter}
    >
      <div>
        <div className={classNames(css.editPanel, { [css.disabled]: isLoading })}>
          {isReportsToEnabled && (
            <Select<Profile["supervisor"]>
              value={supervisorValue?.id}
              label={t("profiles:show.parts.reportsTo")}
              fetchOptions={({ searchTerm, ids }) =>
                fetchProfilesOptions({ term: searchTerm ?? "", value: ids }).then((data) => ({
                  ...data,
                  entries: data.entries
                    .filter((item) => currentProfile.id !== item.id)
                    .map((item) => ({
                      id: item.id,
                      value: getProfileName(item),
                      original: item
                    }))
                }))
              }
              onChange={(option: Option<Profile["supervisor"]>) => {
                setSupervisorValue(option?.original);
                setProfileFieldsToUpdate((prev) => ({ ...prev, supervisorId: option?.id || null }));
              }}
              onClear={
                supervisorValue
                  ? () => {
                      setSupervisorValue(null);
                      setProfileFieldsToUpdate((prev) => ({ ...prev, supervisorId: null }));
                    }
                  : undefined
              }
              tip={t("profiles:show.parts.yourSupervisor")}
              qaId="profile-basic-info-reports-to"
            />
          )}
          {basicCustomTypes.map((customType) => (
            <CustomValuesEditField
              key={customType.name}
              customType={customType}
              values={
                profileFieldsToUpdate[camelize(customType.name)] || profile[camelize(customType.name)] || []
              }
              errors={errors && errors[customType.name]}
              qaIdPrefix="profile-basic-info"
              adminPage={false}
              profile={profile}
              perPage={250}
              handleChange={(values) =>
                setProfileFieldsToUpdate((prev) => ({ ...prev, [camelize(customType.name)]: values }))
              }
              letClear
            />
          ))}
        </div>
      </div>
    </SidePanel>
  );
};
